interface Props {
  /** Oldest → newest. Fewer than two points renders nothing. */
  values: number[];
  /** Stroke colour; the area fill is drawn from the same hue at low opacity. */
  color: string;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * A response-time trace: area fill, a line, and an emphasised endpoint.
 *
 * A single averaged number tells you the level but not the trend — whether a
 * service is drifting slower is the thing worth seeing at a glance, and it
 * costs one small shape.
 */
export function Sparkline({ values, color, width = 116, height = 30, className = '' }: Props) {
  if (values.length < 2) return null;

  const pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  // A flat series would divide by zero; draw it down the middle instead.
  const span = max - min || 1;

  const x = (i: number) => (i / (values.length - 1)) * width;
  const y = (v: number) => {
    const t = max === min ? 0.5 : (v - min) / span;
    return height - pad - t * (height - pad * 2);
  };

  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${width} ${height} L0 ${height} Z`;

  const lastX = width;
  const lastY = y(values[values.length - 1]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={area} fill={color} fillOpacity={0.12} />
      <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
    </svg>
  );
}
